import { useTranslation } from "react-i18next";
import { Button } from "@/components/ui/button";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Switch } from "@/components/ui/switch";
import { MapPin, Loader2 } from "lucide-react";
import { tr } from "@/lib/tr";
import { formatClock, formatRemaining, PRAYER_CITIES } from "@/lib/prayerTimes";
import { prayerLabel, usePrayerReminder } from "@/hooks/usePrayerReminder";
import { cn } from "@/lib/utils";

function cityLabel(id: string): string {
  switch (id) {
    case "riyadh":
      return tr("الرياض", "Riyadh", "Riyad", "ریاض");
    case "jeddah":
      return tr("جدة", "Jeddah", "Djeddah", "جدہ");
    case "makkah":
      return tr("مكة", "Makkah", "La Mecque", "مکہ");
    case "dammam":
      return tr("الدمام", "Dammam", "Dammam", "دمام");
    case "cairo":
      return tr("القاهرة", "Cairo", "Le Caire", "قاہرہ");
    case "dubai":
      return tr("دبي", "Dubai", "Dubaï", "دبئی");
    case "istanbul":
      return tr("إسطنبول", "Istanbul", "Istanbul", "استنبول");
    case "london":
      return tr("لندن", "London", "Londres", "لندن");
    default:
      return id;
  }
}

/**
 * Distinct mosque mark — deliberately NOT a crescent/moon, so the prayer
 * button is never confused with the light/dark theme toggle beside it.
 */
const MosqueIcon = ({ className }: { className?: string }) => (
  <svg
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="1.7"
    strokeLinecap="round"
    strokeLinejoin="round"
    className={className}
    aria-hidden="true"
  >
    <path d="M12 3c1.9 1.5 3 3.1 3 4.7C15 9.5 13.7 11 12 11s-3-1.5-3-3.3C9 6.1 10.1 4.5 12 3Z" />
    <path d="M4 21v-6.2c0-1 .5-1.9 1.4-2.4L12 9l6.6 3.4c.9.5 1.4 1.4 1.4 2.4V21" />
    <path d="M2 21h20" />
    <path d="M10 21v-3a2 2 0 0 1 4 0v3" />
    <path d="M6.5 12V6M17.5 12V6" />
  </svg>
);

export const PrayerReminder = () => {
  const { i18n } = useTranslation();
  const locale = i18n.language || "ar";
  const { settings, active, dayPrayers, next, now, locating, locationError, setEnabled, setCity, requestLocation } =
    usePrayerReminder(locale);

  // Within 20 minutes of the next prayer the button gets a gentle live pulse.
  const soon =
    !!next && next.time.getTime() - now.getTime() <= 20 * 60 * 1000 && next.time.getTime() >= now.getTime();

  return (
    <Popover>
      <PopoverTrigger asChild>
        <Button
          variant="ghost"
          size="sm"
          className={cn(
            "relative shrink-0 gap-1.5 rounded-full border px-2.5 font-medium shadow-sm transition-colors",
            active
              ? "border-accent bg-accent text-accent-foreground hover:bg-accent/90 hover:text-accent-foreground"
              : "border-border bg-secondary text-foreground hover:bg-secondary/80 hover:text-foreground"
          )}
          aria-label={tr("تذكير الصلاة", "Prayer reminder", "Rappel de prière", "نماز یاد دہانی")}
        >
          <MosqueIcon className={cn("h-4 w-4 shrink-0", soon && "animate-pulse")} />

          {active && next && (
            <span className="text-xs font-medium tabular-nums hidden sm:inline">
              {formatRemaining(next.time, now)}
            </span>
          )}
          {soon && (
            <span className="absolute -top-0.5 end-0 flex h-2 w-2">
              <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-destructive opacity-70" />
              <span className="relative inline-flex h-2 w-2 rounded-full bg-destructive" />
            </span>

          )}
        </Button>
      </PopoverTrigger>
      <PopoverContent align="end" className="w-64 p-3">
        <div className="flex items-center justify-between gap-2">
          <span className="text-sm font-medium">
            {tr("تذكير الصلاة", "Prayer reminder", "Rappel de prière", "نماز یاد دہانی")}
          </span>
          <Switch
            checked={settings.enabled}
            onCheckedChange={setEnabled}
            aria-label={tr("تشغيل التذكير", "Enable reminder", "Activer le rappel", "یاد دہانی فعال کریں")}
          />
        </div>

        {!settings.enabled && (
          <p className="mt-2 text-xs text-muted-foreground">
            {tr(
              "التذكير موقوف. شغّله لعرض أوقات الصلاة والتنبيه الهادئ.",
              "Reminder is off. Turn it on to see prayer times and a quiet alert.",
              "Le rappel est désactivé. Activez-le pour voir les heures de prière et une alerte discrète.",
              "یاد دہانی بند ہے۔ نماز کے اوقات اور ہلکی اطلاع کے لیے فعال کریں۔"
            )}
          </p>
        )}

        {settings.enabled && locating && (
          <p className="mt-2 flex items-center gap-2 text-xs text-muted-foreground">
            <Loader2 className="h-3 w-3 animate-spin" />
            {tr("جارٍ تحديد الموقع...", "Locating...", "Localisation...", "مقام کا تعین...")}
          </p>
        )}

        {settings.enabled && locationError && (
          <div className="mt-2 space-y-2">
            <p className="text-xs text-destructive">
              {tr(
                "تعذّر تحديد الموقع. اختر مدينة قريبة:",
                "Could not detect your location. Pick a nearby city:",
                "Localisation impossible. Choisissez une ville proche :",
                "مقام معلوم نہیں ہو سکا۔ قریبی شہر منتخب کریں:"
              )}
            </p>
            <div className="flex flex-wrap gap-1">
              {PRAYER_CITIES.map((c) => (
                <Button
                  key={c.id}
                  variant={settings.cityId === c.id ? "secondary" : "outline"}
                  size="sm"
                  className="h-6 px-2 text-xs"
                  onClick={() => setCity(c.id, c.lat, c.lng)}
                >
                  {cityLabel(c.id)}
                </Button>
              ))}
            </div>
          </div>
        )}

        {active && dayPrayers.length > 0 && (
          <ul className="mt-3 space-y-1">
            {dayPrayers.map((p) => {
              const isNext = next?.key === p.key && next.time.getTime() === p.time.getTime();
              return (
                <li
                  key={p.key}
                  className={cn(
                    "flex items-center justify-between rounded px-2 py-1 text-sm",
                    isNext ? "bg-accent/10 font-medium text-foreground" : "text-muted-foreground"
                  )}
                >
                  <span>{prayerLabel(p.key)}</span>
                  <span className="tabular-nums">{formatClock(p.time, locale)}</span>
                </li>
              );
            })}
          </ul>
        )}

        <Button
          variant="ghost"
          size="sm"
          className="mt-3 w-full justify-start gap-2 text-xs"
          disabled={!settings.enabled || locating}
          onClick={requestLocation}
        >
          <MapPin className="h-3.5 w-3.5" />
          {tr("تحديث الموقع", "Update location", "Actualiser la position", "مقام اپ ڈیٹ کریں")}
        </Button>
      </PopoverContent>
    </Popover>
  );
};
